import {HttpClientModule, HTTP_INTERCEPTORS} from '@angular/common/http';
import {ErrorHandler, NgModule} from '@angular/core';
import {FormsModule, ReactiveFormsModule} from '@angular/forms';
import {BrowserModule} from '@angular/platform-browser';
import {BrowserAnimationsModule} from '@angular/platform-browser/animations';
import {ColorPickerModule} from 'ngx-color-picker';
import {NgSelectModule} from '@ng-select/ng-select';
import * as Sentry from '@sentry/angular-ivy';
import {Router} from '@angular/router';
import {AppComponent} from './app.component';
import {routing} from './app.routing';
import {ClubOverviewModule} from '~features/club-overview/club-overview.module';
import {CrmComponent} from '~features/crm/crm.component';
import {CustomerOverviewModule} from '~features/customer-overview/customer-overview.module';
import {HomeRemindersComponent} from '~features/home-reminders/components/home-reminders.component';
import {HomeComponent} from '~features/home/home.component';
import {SearchCustomerModule} from '~features/search-customer/search-customer.module';
import {LoginComponent} from '~features/login/login.component';
import {ManageModule} from '~features/manage/manage.module';
import {PasswordResetComponent} from '~features/login/components/password-reset/password-reset.component';
import {LoginExternalComponent} from '~features/login/components/login-external/login-external.component';
import {CustomerMergeGuard} from 'src/app/core/guards';
import {DeactivateGuard} from 'src/app/core/models';
import {FooterService} from '~core/components/footer/footer.service';
import {NavigationModule} from '~core/components/navigation.module';
import {UserManagementService} from 'src/app/core/services';
import {ClubSelectedResolver, CustomerResolver, TixxSelectResolver} from 'src/app/core/resolvers';
import {TranslationsModule} from '~shared/translations/translations.module';
import {
    BasicStatusTranslationService,
    BreadcrumbTranslationService, CampaignActivityTranslationService,
    CampaignStatusTranslationsService,
    InvoiceOrderStatusTranslationsService,
    MembershipStatusTranslationsService,
    ProductSettingsTranslationService,
    SalutationsTranslationService,
} from 'src/app/shared/translations';
import {
    ActivityHistoryService,
    AddressService,
    AttributeService,
    AuthenticationService,
    CampaignService,
    ClubService,
    ContactManagementService,
    CountryService,
    CustomerService, ExportService,
    InvoiceService,
    MembershipService,
    MerchandisingService,
    PaginationService,
    QueryParameterService,
    StorageService,
    UserService,
} from 'src/app/core/services';
import {ErrorInterceptor, JwtInterceptor} from 'src/app/core/helpers';
import {
    AuthChildGuard,
    AuthGuard,
    ClubAttributesGuard,
    ClubListGuard,
    ClubSettingsGuard,
    UserListGuard,
} from 'src/app/core/guards';
import {ModalModule} from "~shared/components/elements/modal.module";
import {FormValidationModule} from "~core/components/form-validation/form-validation.module";
import {FormFieldsModule} from "~shared/components/form-fields/form-fields.module";
import {ButtonsModule} from "~shared/components/buttons/buttons.module";
import {TablesModule} from "~shared/components/tables/tables.module";
import {NotificationModule} from "~core/components/notification/notification.module";
import {DirectivesModule} from "~shared/directives/directives.module";
import {APP_BASE_HREF, PlatformLocation} from "@angular/common";
import {CreateCustomerModule} from "~features/create-customer/create-customer.module";
import {MergeCustomerModule} from "~features/customer-merger/merge-customer.module";
import {ResolvedHomeRemindersComponent} from "~features/home-reminders/pages/resolved-home-reminders.component";
import {OpenHomeRemindersComponent} from "~features/home-reminders/pages/open-home-reminders.component";
import {ImportsComponent} from "~features/import-customer/pages/import-customer.component";
import {UploadImportComponent} from "~features/import-customer/components/upload-import/upload-import.component";
import {ImportHistoryComponent} from "~features/import-customer/pages/import-history/import-history.component";
import {TixxSelectModule} from "~features/tixx-select/tixx-select.module";
import {MaintenanceComponent} from "~core/routing/maintenance-page/maintenance.component";
import {MaintenanceEnComponent} from "~core/routing/maintenance-page/maintenance.en";
import {MaintenanceDeComponent} from "~core/routing/maintenance-page/maintenance.de";
import {MaintenanceEsComponent} from "~core/routing/maintenance-page/maintenance.es";

/**
 * App module bootstrap file.
 */
@NgModule({
    imports: [
        BrowserModule,
        ModalModule,
        ReactiveFormsModule,
        HttpClientModule,
        routing,
        FormsModule,
        BrowserAnimationsModule,
        TranslationsModule,
        NavigationModule,
        ManageModule,
        SearchCustomerModule,
        CustomerOverviewModule,
        ClubOverviewModule,
        DirectivesModule,
        ColorPickerModule,
        FormValidationModule,
        FormFieldsModule,
        ButtonsModule,
        TablesModule,
        NgSelectModule,
        NotificationModule,
        TixxSelectModule,
        CreateCustomerModule,
        MergeCustomerModule
    ],
    declarations: [
        AppComponent,
        HomeComponent,
        LoginComponent,
        LoginExternalComponent,
        ImportsComponent,
        UploadImportComponent,
        ImportHistoryComponent,
        CrmComponent,
        PasswordResetComponent,
        HomeRemindersComponent,
        OpenHomeRemindersComponent,
        ResolvedHomeRemindersComponent,
        MaintenanceComponent,
        MaintenanceEnComponent,
        MaintenanceDeComponent,
        MaintenanceEsComponent
    ],
    providers: [
        AuthGuard,
        AuthChildGuard,
        ClubAttributesGuard,
        UserListGuard,
        ClubListGuard,
        ClubSettingsGuard,
        CustomerMergeGuard,
        ClubSelectedResolver,
        CustomerResolver,
        TixxSelectResolver,
        AuthenticationService,
        CustomerService,
        CountryService,
        AddressService,
        QueryParameterService,
        ContactManagementService,
        ClubService,
        ActivityHistoryService,
        PaginationService,
        AttributeService,
        ExportService,
        MerchandisingService,
        UserService,
        UserManagementService,
        MembershipService,
        MembershipStatusTranslationsService,
        InvoiceService,
        InvoiceOrderStatusTranslationsService,
        CampaignActivityTranslationService,
        CampaignService,
        StorageService,
        CampaignStatusTranslationsService,
        BreadcrumbTranslationService,
        SalutationsTranslationService,
        BasicStatusTranslationService,
        ProductSettingsTranslationService,
        DeactivateGuard,
        FooterService,
        {
            provide: ErrorHandler,
            useValue: Sentry.createErrorHandler({
                showDialog: false,
            }),
        },
        {
            provide: Sentry.TraceService,
            deps: [Router],
        },
        {
            provide: APP_BASE_HREF,
            useFactory: (s: PlatformLocation) => s.getBaseHrefFromDOM(),
            deps: [PlatformLocation]
        },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: JwtInterceptor,
            multi: true
        },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: ErrorInterceptor,
            multi: true
        }
    ],
    bootstrap: [AppComponent]
})
export class AppModule {
    constructor() {
    }
}
